import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { diffLines } from "diff";
import { Code2, ShieldCheck, Search, Copy, Download, GitCompare, Lock, Wand2, Folder, FileCode } from "lucide-react";

// Category 7 — Code Review Tab. Read-only by default: changes route back through the prompt box
// (Suggest an edit -> metered prompt flow), never a silent direct edit. The diff view and the agent
// stream share ONE timeline (the project process-log), so they cannot drift apart.

const KW = {
  python: new Set("def class return import from if elif else for while try except finally with as async await lambda yield raise True False None and or not in is pass break continue".split(" ")),
  javascript: new Set("const let var function return if else for while try catch finally class extends import from export default await async new this typeof instanceof null undefined true false".split(" ")),
  typescript: new Set("const let var function return if else for while try catch finally class extends implements interface type import from export default await async new this typeof null undefined true false public private readonly".split(" ")),
};
const escTok = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function highlight(code, language) {
  const kws = KW[language] || KW.javascript;
  const re = /(\/\/[^\n]*|#[^\n]*)|("[^"\n]*"|'[^'\n]*')|(\d+(?:\.\d+)?)|([A-Za-z_$][A-Za-z0-9_$]*)/g;
  let out = "";
  let last = 0;
  const src = code || "";
  let m = re.exec(src);
  while (m !== null) {
    out += escTok(src.slice(last, m.index));
    if (m[1]) out += '<span style="color:#7b7b83">' + escTok(m[1]) + "</span>";
    else if (m[2]) out += '<span style="color:#e09c9a">' + escTok(m[2]) + "</span>";
    else if (m[3]) out += '<span style="color:#9adee0">' + m[3] + "</span>";
    else if (m[4]) out += kws.has(m[4]) ? '<span style="color:#c4b5fd;font-weight:600">' + m[4] + "</span>" : escTok(m[4]);
    last = m.index + m[0].length;
    m = re.exec(src);
  }
  out += escTok(src.slice(last));
  return out;
}

function computeRows(files) {
  const rows = [];
  const seen = new Set();
  const sorted = files.map((f, i) => ({ path: f.path, i })).sort((a, b) => a.path.localeCompare(b.path));
  sorted.forEach((f) => {
    const segs = f.path.split("/");
    for (let d = 0; d < segs.length - 1; d++) {
      const key = segs.slice(0, d + 1).join("/");
      if (!seen.has(key)) {
        seen.add(key);
        rows.push({ type: "dir", name: segs[d], depth: d, key });
      }
    }
    rows.push({ type: "file", name: segs[segs.length - 1], depth: segs.length - 1, index: f.i });
  });
  return rows;
}

function search(files, q) {
  const out = [];
  const needle = q.toLowerCase();
  files.forEach((f, fi) => {
    (f.preview || "").split("\n").forEach((line, li) => {
      if (line.toLowerCase().indexOf(needle) !== -1) {
        out.push({ fi, path: f.path, line: li + 1, text: line.trim().slice(0, 120) });
      }
    });
  });
  return out.slice(0, 100);
}

function diffRows(before, after) {
  const rows = [];
  diffLines(before || "", after || "").forEach((p) => {
    const prefix = p.added ? "+ " : p.removed ? "- " : "  ";
    const color = p.added ? "#9adee0" : p.removed ? "#e09c9a" : "inherit";
    const bg = p.added ? "rgba(154,222,224,.10)" : p.removed ? "rgba(224,156,154,.10)" : "transparent";
    p.value.replace(/\n$/, "").split("\n").forEach((ln) => rows.push({ prefix, color, bg, ln }));
  });
  return rows;
}

export default function CodeReviewTab({ projectId }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(0);
  const [q, setQ] = useState("");
  const [view, setView] = useState("code");
  const [diff, setDiff] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [stepId, setStepId] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [instruction, setInstruction] = useState("");

  useEffect(() => {
    api.get(`/builder/${projectId}/code`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/builder/${projectId}/code/diff`).then((r) => setDiff(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/process-log`).then((r) => {
      const evs = r.data.events || [];
      setTimeline(evs);
      if (evs.length) setStepId(evs[evs.length - 1].id);
    }).catch(() => {});
  }, [projectId]);

  if (!data) return <p className="k-muted">Loading…</p>;
  const files = data.files || [];
  const rows = computeRows(files);
  const current = files[sel];
  const hits = q.trim() ? search(files, q) : [];

  const copyFile = () => {
    navigator.clipboard.writeText(current?.preview || "").then(() => toast.success("Code copied")).catch(() => {});
  };
  const exportFile = () => {
    const blob = new Blob([current?.preview || ""], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = (current?.path || "file").split("/").pop();
    a.click();
  };
  const submitSuggest = async () => {
    if (!instruction.trim()) return;
    try {
      const res = await api.post(`/builder/${projectId}/code/suggest-edit`, {
        file_path: current?.path || "", snippet: (current?.preview || "").slice(0, 800), instruction,
      });
      toast.success(`Sent through the prompt flow · ${res.data.remaining} left today`);
      setSuggestOpen(false);
      setInstruction("");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="code-review-tab">
      <div className="k-container" style={{ padding: 14, marginBottom: 14, display: "flex", alignItems: "flex-start", gap: 10 }}>
        <ShieldCheck size={18} className="k-tag-green" aria-hidden />
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="k-badge" data-testid="code-readonly-badge"><Lock size={12} /> Read-only</span>
            <span className="k-muted" style={{ fontSize: 12 }}>Changes route through the prompt box — never a silent direct edit.</span>
          </div>
          <p className="k-muted" style={{ fontSize: 12, margin: "6px 0 0" }}>{data.self_healing?.note}</p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        <div style={{ display: "flex", border: "1px solid var(--control)", borderRadius: 8, overflow: "hidden" }} role="group" aria-label="View mode">
          <button className={`k-btn ${view === "code" ? "k-btn-primary" : "k-btn-ghost"}`} data-testid="code-view-code" onClick={() => setView("code")} style={{ padding: "5px 12px", fontSize: 12 }}><Code2 size={13} /> Code</button>
          <button className={`k-btn ${view === "diff" ? "k-btn-primary" : "k-btn-ghost"}`} data-testid="code-view-diff" onClick={() => setView("diff")} style={{ padding: "5px 12px", fontSize: 12 }}><GitCompare size={13} /> Diff</button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 200 }}>
          <Search size={13} className="k-muted" />
          <input className="k-control" data-testid="code-search" placeholder="Search across the whole project…" value={q} onChange={(e) => setQ(e.target.value)} style={{ fontSize: 13 }} />
        </div>
        <button className="k-btn k-btn-ghost" data-testid="code-copy" onClick={copyFile} style={{ padding: "5px 10px", fontSize: 12 }}><Copy size={13} /> Copy</button>
        <button className="k-btn k-btn-ghost" data-testid="code-export" onClick={exportFile} style={{ padding: "5px 10px", fontSize: 12 }}><Download size={13} /> Export</button>
        <button className="k-btn k-btn-primary" data-testid="code-suggest-edit" onClick={() => setSuggestOpen(true)} style={{ padding: "5px 10px", fontSize: 12 }}><Wand2 size={13} /> Suggest an edit</button>
      </div>

      <SearchList hits={hits} show={!!q.trim()} onPick={(fi) => { setSel(fi); setView("code"); setQ(""); }} />

      <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 14 }}>
        <div className="k-container" data-testid="code-file-tree" style={{ padding: 8, maxHeight: 520, overflow: "auto" }}>
          {rows.map((r) => <TreeRow key={(r.key || "") + r.name + r.index} row={r} selected={sel} onSelect={setSel} />)}
        </div>
        <div className="k-container" style={{ padding: 14, maxHeight: 520, overflow: "auto" }}>
          <CodeView show={view === "code"} file={current} />
          <DiffView show={view === "diff"} diff={diff} timeline={timeline} stepId={stepId} onStep={setStepId} />
        </div>
      </div>

      <p className="k-muted" style={{ fontSize: 12, marginTop: 10 }}>{data.note}</p>

      <SuggestModal open={suggestOpen} path={current?.path} value={instruction}
        onChange={setInstruction} onCancel={() => setSuggestOpen(false)} onSend={submitSuggest} />
    </div>
  );
}

function TreeRow({ row, selected, onSelect }) {
  if (row.type === "dir") {
    return (
      <div className="k-muted" style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, padding: "4px 8px", paddingLeft: 8 + row.depth * 12 }}>
        <Folder size={12} /> {row.name}
      </div>
    );
  }
  return (
    <button data-testid={`code-file-${row.index}`} onClick={() => onSelect(row.index)}
      className={`k-badge ${selected === row.index ? "k-badge-accent" : ""}`}
      style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", textAlign: "left", marginBottom: 2, padding: "5px 8px", paddingLeft: 8 + row.depth * 12 }}>
      <FileCode size={12} /> {row.name}
    </button>
  );
}

function SearchList({ hits, show, onPick }) {
  if (!show) return null;
  return (
    <div className="k-container" data-testid="code-search-results" style={{ padding: 10, marginBottom: 10 }}>
      <p className="k-muted" style={{ fontSize: 12, margin: "0 0 6px" }}>{hits.length} match(es)</p>
      {hits.map((r, i) => (
        <button key={i} data-testid={`code-search-hit-${i}`} className="k-btn k-btn-ghost" onClick={() => onPick(r.fi)}
          style={{ display: "block", width: "100%", textAlign: "left", fontSize: 12, padding: "4px 8px" }}>
          <span className="mono" style={{ color: "var(--accent)" }}>{r.path}:{r.line}</span> <span className="k-muted">{r.text}</span>
        </button>
      ))}
    </div>
  );
}

function CodeView({ show, file }) {
  if (!show) return null;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span className="k-muted mono" style={{ fontSize: 12 }}>{file?.path}</span>
        <span className="k-badge" style={{ fontSize: 10 }}>{file?.language || "text"}</span>
      </div>
      <pre className="mono" data-testid="code-preview" style={{ fontSize: 12, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}
        dangerouslySetInnerHTML={{ __html: highlight(file?.preview || "—", file?.language) }} />
    </div>
  );
}

function DiffView({ show, diff, timeline, stepId, onStep }) {
  if (!show) return null;
  const changed = diff?.changed || [];
  const emptyMsg = diff?.has_history
    ? "No file changes in this step."
    : "No prior build to compare against yet — the most recent build is the first.";
  return (
    <div data-testid="code-diff-view">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <span className="k-muted" style={{ fontSize: 12 }}>Build step:</span>
        <select className="k-control" data-testid="diff-step-select" value={stepId} onChange={(e) => onStep(e.target.value)} style={{ width: "auto", fontSize: 12, padding: "4px 6px" }}>
          {timeline.length === 0 ? <option value="">No build steps yet</option> : null}
          {timeline.map((ev) => <option key={ev.id} value={ev.id}>{ev.step} · {String(ev.at).slice(11, 19)}</option>)}
        </select>
        <span className="k-muted" style={{ fontSize: 11 }}>Same timeline as the agent stream &amp; preview scrubber.</span>
      </div>
      {changed.length === 0 ? <p className="k-muted" style={{ fontSize: 13 }} data-testid="diff-empty">{emptyMsg}</p> : null}
      {changed.map((c, i) => <DiffBlock key={c.path} change={c} index={i} />)}
      {diff?.note ? <p className="k-muted" style={{ fontSize: 11, marginTop: 8 }}>{diff.note}</p> : null}
    </div>
  );
}

function DiffBlock({ change, index }) {
  const rows = diffRows(change.before, change.after);
  return (
    <div data-testid={`diff-file-${index}`} style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span className="mono" style={{ fontSize: 12, color: "var(--accent)" }}>{change.path}</span>
        <span className="k-badge" style={{ fontSize: 10 }}>{change.status}</span>
      </div>
      <pre className="mono" style={{ fontSize: 12, margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
        {rows.map((r, k) => <div key={k} style={{ background: r.bg, color: r.color, padding: "0 6px" }}>{r.prefix}{r.ln}</div>)}
      </pre>
    </div>
  );
}

function SuggestModal({ open, path, value, onChange, onCancel, onSend }) {
  if (!open) return null;
  return (
    <div data-testid="suggest-edit-modal" role="dialog" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
      <div className="k-container" style={{ padding: 20, width: "min(560px,92vw)" }}>
        <h3 className="k-heading" style={{ fontSize: 17, marginTop: 0, display: "flex", alignItems: "center", gap: 8 }}><Wand2 size={16} /> Suggest an edit</h3>
        <p className="k-muted" style={{ fontSize: 13 }}>
          This does not edit code directly. It composes a prompt about <span className="mono">{path}</span> and sends it through the normal prompt flow — the self-healing system stays the single source of truth for how code changes. Metered like a Plan-mode generation.
        </p>
        <textarea className="k-control" data-testid="suggest-edit-input" rows={4} autoFocus value={value}
          onChange={(e) => onChange(e.target.value)} placeholder="Describe the change you'd like the agent to make…" style={{ fontSize: 13, marginTop: 6 }} />
        <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
          <button className="k-btn k-btn-ghost" data-testid="suggest-edit-cancel" onClick={onCancel}>Cancel</button>
          <button className="k-btn k-btn-primary" data-testid="suggest-edit-send" onClick={onSend}>Send through prompt flow</button>
        </div>
      </div>
    </div>
  );
}
